import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { FamilyProvider } from './context/FamilyContext';
import { CompanyDocumentTitle } from './utils/companyDetails';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InviteRsvpPage from './pages/InviteRsvpPage';
import DashboardLayout from './layouts/DashboardLayout';
import ProfilePage from './pages/ProfilePage';
import MatchmakingPage from './pages/MatchmakingPage';
import ContractsPage from './pages/ContractsPage';
import WishlistPage from './pages/WishlistPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminRolesPage from './pages/AdminRolesPage';
import AdminPermissionsPage from './pages/AdminPermissionsPage';
import AdminPermissionsCrudPage from './pages/AdminPermissionsCrudPage';
import AdminContractsPage from './pages/AdminContractsPage';
import AdminLovPage from './pages/AdminLovPage';
import MessagingPage from './pages/MessagingPage';
import MeetingsPage from './pages/MeetingsPage';
import DashboardOverviewPage from './pages/DashboardOverviewPage';
import PrivateRoute from './components/PrivateRoute';
// Phase 2
import RitualPlanningPage from './pages/RitualPlanningPage';
import RitualItemsPage from './pages/RitualItemsPage';
import VenuesPage from './pages/VenuesPage';
import VendorsPage from './pages/VendorsPage';
import AdminVenuesPage from './pages/AdminVenuesPage';
import AdminVendorsPage from './pages/AdminVendorsPage';
import AdminRitualItemsPage from './pages/AdminRitualItemsPage';
// Phase 3
import CateringPage from './pages/CateringPage';
import GuestListPage from './pages/GuestListPage';
import TransportationPage from './pages/TransportationPage';
import EntertainmentPage from './pages/EntertainmentPage';
import StaffingPage from './pages/StaffingPage';
import AdminCaterersPage from './pages/AdminCaterersPage';
import AdminEntertainmentPage from './pages/AdminEntertainmentPage';
// Gap modules
import ParkingPage from './pages/ParkingPage';
import DecorationPage from './pages/DecorationPage';
import SeatingPage from './pages/SeatingPage';
// Phase 4
import TimelinePage from './pages/TimelinePage';
import BudgetPage from './pages/BudgetPage';
import NotificationsPage from './pages/NotificationsPage';
import CommunicationPage from './pages/CommunicationPage';
// Gap module pages (VIP Escort, Caterer Comparison)
import VipEscortPage from './pages/VipEscortPage';
import CatererComparisonPage from './pages/CatererComparisonPage';
// Phase 5
import HoneymoonPage from './pages/HoneymoonPage';
import AdminHoneymoonPage from './pages/AdminHoneymoonPage';
import InvitationPage from './pages/InvitationPage';
import AccommodationPage from './pages/AccommodationPage';
import GiftRegistryPage from './pages/GiftRegistryPage';
import BeautyServicesPage from './pages/BeautyServicesPage';
import PhotoAlbumPage from './pages/PhotoAlbumPage';
import AstrologyPage from './pages/AstrologyPage';
import AdminBeautyServicesPage from './pages/AdminBeautyServicesPage';
// Admin Phase 1–4
import AdminWishlistTemplatesPage from './pages/AdminWishlistTemplatesPage';
import AdminDecorationThemesPage from './pages/AdminDecorationThemesPage';
import AdminStaffingPage from './pages/AdminStaffingPage';
import AdminTransportationPage from './pages/AdminTransportationPage';
import AdminParkingPage from './pages/AdminParkingPage';
import AdminTimelinePage from './pages/AdminTimelinePage';
import AdminBudgetCategoriesPage from './pages/AdminBudgetCategoriesPage';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import AdminInvitationPage from './pages/AdminInvitationPage';
import AdminAstrologyPage from './pages/AdminAstrologyPage';
import AdminServicesPage from './pages/AdminServicesPage';
import AdminServiceCategoriesPage from './pages/AdminServiceCategoriesPage';
import AdminCompanyPage from './pages/AdminCompanyPage';
import AdminBulkImportPage from './pages/AdminBulkImportPage';
import AdminApiDocsPage from './pages/AdminApiDocsPage';
import AdminAuditLogsPage from './pages/AdminAuditLogsPage';
import AdminLegalPage from './pages/AdminLegalPage';
import LegalPage from './pages/LegalPage';
import GrievancePage from './pages/GrievancePage';
import PrivacyCenterPage from './pages/PrivacyCenterPage';
import AdminCompliancePage from './pages/AdminCompliancePage';
import AdminMarketingPage from './pages/AdminMarketingPage';
import AdminMessageTemplatesPage from './pages/AdminMessageTemplatesPage';
import AdminSurveysPage from './pages/AdminSurveysPage';
import SurveyRespondPage from './pages/SurveyRespondPage';
import VendorInvitePage from './pages/VendorInvitePage';
// Service Questionnaire Wizard
import WishlistWizardPage from './pages/WishlistWizardPage';
import AdminQuestionsPage from './pages/AdminQuestionsPage';
import AdminWishlistApprovalPage from './pages/AdminWishlistApprovalPage';
import AdminEmailSettingsPage from './pages/AdminEmailSettingsPage';
import AdminEmailTemplatesPage from './pages/AdminEmailTemplatesPage';
import AdminNotificationTriggersPage from './pages/AdminNotificationTriggersPage';
import VendorServicesPage from './pages/VendorServicesPage';
import ServiceProvidersPage from './pages/ServiceProvidersPage';
import MyVendorsPage from './pages/MyVendorsPage';
import VendorBookingsPage from './pages/VendorBookingsPage';
import AdminVendorServicesPage from './pages/AdminVendorServicesPage';

export default function App() {
  return (
    <AuthProvider>
      <FamilyProvider>
      <CompanyDocumentTitle />
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<InviteRsvpPage />} />
        <Route path="/legal/grievance" element={<GrievancePage />} />
        <Route path="/vendor-invite/:token" element={<VendorInvitePage />} />
        <Route path="/survey/:token" element={<SurveyRespondPage />} />
        <Route path="/legal/:slug" element={<LegalPage />} />

        {/* Protected */}
        <Route element={<PrivateRoute />}>
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<Navigate to="/dashboard/overview" replace />} />
            <Route path="/dashboard/overview" element={<DashboardOverviewPage />} />
            <Route path="/dashboard/profile" element={<ProfilePage />} />
            <Route path="/dashboard/matchmaking" element={<MatchmakingPage />} />
            <Route path="/dashboard/contracts" element={<ContractsPage />} />
            <Route path="/dashboard/wishlist" element={<WishlistPage />} />
            <Route path="/dashboard/wishlist-wizard" element={<WishlistWizardPage />} />
            <Route path="/dashboard/messages" element={<MessagingPage />} />
            <Route path="/dashboard/meetings" element={<MeetingsPage />} />
            {/* Phase 2 */}
            <Route path="/dashboard/rituals" element={<RitualPlanningPage />} />
            <Route path="/dashboard/ritual-items" element={<RitualItemsPage />} />
            <Route path="/dashboard/venues" element={<VenuesPage />} />
            <Route path="/dashboard/vendors" element={<VendorsPage />} />
            {/* Phase 3 */}
            <Route path="/dashboard/catering" element={<CateringPage />} />
            <Route path="/dashboard/guests" element={<GuestListPage />} />
            <Route path="/dashboard/transportation" element={<TransportationPage />} />
            <Route path="/dashboard/entertainment" element={<EntertainmentPage />} />
            <Route path="/dashboard/staffing" element={<StaffingPage />} />
            {/* Gap modules */}
            <Route path="/dashboard/parking" element={<ParkingPage />} />
            <Route path="/dashboard/decoration" element={<DecorationPage />} />
            <Route path="/dashboard/seating" element={<SeatingPage />} />
            {/* Phase 4 */}
            <Route path="/dashboard/timeline" element={<TimelinePage />} />
            <Route path="/dashboard/budget" element={<BudgetPage />} />
            <Route path="/dashboard/notifications" element={<NotificationsPage />} />
            <Route path="/dashboard/communication" element={<CommunicationPage />} />
            {/* Gap module pages */}
            <Route path="/dashboard/vip-escorts" element={<VipEscortPage />} />
            <Route path="/dashboard/caterer-comparison" element={<CatererComparisonPage />} />
            {/* Phase 5 */}
            <Route path="/dashboard/honeymoon" element={<HoneymoonPage />} />
            <Route path="/dashboard/invitations" element={<InvitationPage />} />
            <Route path="/dashboard/accommodation" element={<AccommodationPage />} />
            <Route path="/dashboard/gift-registry" element={<GiftRegistryPage />} />
            <Route path="/dashboard/beauty-services" element={<BeautyServicesPage />} />
            <Route path="/dashboard/photo-albums" element={<PhotoAlbumPage />} />
            <Route path="/dashboard/astrology" element={<AstrologyPage />} />
            <Route path="/dashboard/my-services" element={<VendorServicesPage />} />
            <Route path="/dashboard/service-providers" element={<ServiceProvidersPage />} />
            <Route path="/dashboard/my-vendors" element={<MyVendorsPage />} />
            <Route path="/dashboard/vendor-bookings" element={<VendorBookingsPage />} />

            {/* Admin-only */}
            <Route path="/dashboard/admin/users" element={<AdminUsersPage />} />
            <Route path="/dashboard/admin/roles" element={<AdminRolesPage />} />
            <Route path="/dashboard/admin/permissions" element={<AdminPermissionsPage />} />
            <Route path="/dashboard/admin/permissions-crud" element={<AdminPermissionsCrudPage />} />
            <Route path="/dashboard/admin/contracts" element={<AdminContractsPage />} />
            <Route path="/dashboard/admin/services" element={<AdminServicesPage />} />
            <Route path="/dashboard/admin/service-categories" element={<AdminServiceCategoriesPage />} />
            <Route path="/dashboard/admin/lov" element={<AdminLovPage />} />
            <Route path="/dashboard/admin/venues" element={<AdminVenuesPage />} />
            <Route path="/dashboard/admin/vendors" element={<AdminVendorsPage />} />
            <Route path="/dashboard/admin/vendors/:vendorId" element={<AdminVendorServicesPage />} />
            <Route path="/dashboard/admin/ritual-items" element={<AdminRitualItemsPage />} />
            <Route path="/dashboard/admin/caterers" element={<AdminCaterersPage />} />
            <Route path="/dashboard/admin/entertainment" element={<AdminEntertainmentPage />} />
            <Route path="/dashboard/admin/honeymoon" element={<AdminHoneymoonPage />} />
            <Route path="/dashboard/admin/beauty-services" element={<AdminBeautyServicesPage />} />
            <Route path="/dashboard/admin/invitations" element={<AdminInvitationPage />} />
            <Route path="/dashboard/admin/astrology" element={<AdminAstrologyPage />} />
            <Route path="/dashboard/admin/wishlist-templates" element={<AdminWishlistTemplatesPage />} />
            <Route path="/dashboard/admin/decoration-themes" element={<AdminDecorationThemesPage />} />
            <Route path="/dashboard/admin/staffing" element={<AdminStaffingPage />} />
            <Route path="/dashboard/admin/transportation" element={<AdminTransportationPage />} />
            <Route path="/dashboard/admin/parking" element={<AdminParkingPage />} />
            <Route path="/dashboard/admin/timeline" element={<AdminTimelinePage />} />
            <Route path="/dashboard/admin/budget-categories" element={<AdminBudgetCategoriesPage />} />
            <Route path="/dashboard/admin/admin-notifications" element={<AdminNotificationsPage />} />
            <Route path="/dashboard/admin/company" element={<AdminCompanyPage />} />
            <Route path="/dashboard/admin/bulk-import" element={<AdminBulkImportPage />} />
            <Route path="/dashboard/admin/api-docs" element={<AdminApiDocsPage />} />
            <Route path="/dashboard/admin/audit-logs" element={<AdminAuditLogsPage />} />
            <Route path="/dashboard/admin/legal" element={<AdminLegalPage />} />
            <Route path="/dashboard/admin/compliance" element={<AdminCompliancePage />} />
            <Route path="/dashboard/admin/marketing" element={<AdminMarketingPage />} />
            <Route path="/dashboard/admin/message-templates" element={<AdminMessageTemplatesPage />} />
            <Route path="/dashboard/admin/surveys" element={<AdminSurveysPage />} />
            <Route path="/dashboard/privacy" element={<PrivacyCenterPage />} />
            <Route path="/dashboard/admin/questions" element={<AdminQuestionsPage />} />
            <Route path="/dashboard/admin/wishlist-approvals" element={<AdminWishlistApprovalPage />} />
            <Route path="/dashboard/admin/email-settings" element={<AdminEmailSettingsPage />} />
            <Route path="/dashboard/admin/email-templates" element={<AdminEmailTemplatesPage />} />
            <Route path="/dashboard/admin/notification-triggers" element={<AdminNotificationTriggersPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </FamilyProvider>
    </AuthProvider>
  );
}
